import { CatalogFlow, normalizeCatalogNode } from './catalogDoc';
import { GraphDevice } from './graph';
import {
  CatalogWizardArgs,
  ChainState,
  asGroup,
  ensureController,
  ensureDashboard,
  ensureDevice,
  ensurePoint,
  finishChain,
  link,
  startChain,
  upsertDevice,
  wireToMqtt,
} from './catalogChain';

/** MQTT on, stacklight instance in devices[]. Strips stay the WS2812 rows already in settings. */
export function applyStacklightHardware(
  values: Record<string, unknown>,
  device: GraphDevice,
): Record<string, unknown> {
  const instance: GraphDevice = {
    ...device,
    id: String(device.id || 'stacklight').trim() || 'stacklight',
    type: 'stacklight',
    enable: device.enable !== false,
  };
  const zigbeeOn = asGroup(values.zigbee).enable === true;
  const mqtt = asGroup(values.mqtt);
  return {
    ...values,
    mqtt: zigbeeOn ? mqtt : { ...mqtt, enable: true },
    devices: upsertDevice(values, instance),
  };
}

/**
 * Reverse on the same bus the status already uses.
 * The widget stays in the cloud: command publishes on that bus.
 * The device subscribes into the status point, never into the stacklight instance.
 */
export function linkStacklightCommand(
  state: ChainState,
  args: {
    pointNodeId: string;
    controllerNodeId?: string;
    dashNodeId?: string;
    mqttNodeId?: string;
    zigbee?: boolean;
    /** Dashboard port the state cable uses. `command` means the `in` port. */
    dashPort?: string;
    /** One cable: the radio feeds the point and the point reports back on it. */
    both?: boolean;
    x: number;
    y: number;
  },
): void {
  const dashPort = args.dashPort || 'command';
  if (args.zigbee) {
    if (!args.controllerNodeId) {
      return;
    }
    const zb = ensureDevice(state, {
      of: 'zigbee',
      deviceType: 'zigbee',
      x: args.x,
      y: args.y,
    });
    link(state, `${args.controllerNodeId}.host`, `${zb.id}.host`);
    link(state, `${zb.id}.out`, `${args.pointNodeId}.command`, true);
    return;
  }
  if (!args.mqttNodeId) {
    return;
  }
  const publishFrom = `${args.pointNodeId}.value`;
  const publishTo = `${args.mqttNodeId}.publish`;
  const publish = state.edges.find((edge) => edge.from === publishFrom && edge.to === publishTo);
  if (publish) {
    publish.both = true;
  } else {
    link(state, publishFrom, publishTo, true);
  }
  if (args.dashNodeId) {
    const showTo = `${args.dashNodeId}.${dashPort === 'command' ? 'in' : dashPort}`;
    const show = state.edges.find((edge) => edge.from === `${args.mqttNodeId}.subscribe` && edge.to === showTo);
    if (show) {
      show.both = true;
    } else {
      link(state, `${args.mqttNodeId}.subscribe`, showTo, true);
    }
  }
}

export function mergeStacklightCatalog(doc: CatalogFlow, args: CatalogWizardArgs): CatalogFlow {
  const state = startChain(doc);
  const of = String(args.device.id || 'stacklight').trim() || 'stacklight';
  const controller = ensureController(state, args);
  const device = ensureDevice(state, {
    of,
    deviceType: 'stacklight',
    x: state.x0 + 200,
    y: state.y0,
  });
  if (args.signal) {
    device.signal = args.signal;
  }
  link(state, `${controller.id}.host`, `${device.id}.host`);
  const status = ensurePoint(state, {
    of,
    key: 'status',
    x: state.x0 + 400,
    y: state.y0,
    deviceId: device.id,
  });
  const zigbee = args.wireZigbeeUplink === true;
  const mqtt = zigbee
    ? undefined
    : wireToMqtt(state, {
      point: status,
      topic: '{base}',
      y: state.y0,
      xCalc: state.x0 + 560,
      xMqtt: state.x0 + 560,
    });
  if (args.widget === 'stacklight') {
    const dash = ensureDashboard(state, {
      widgetType: 'stacklight',
      title: args.hostname ? `${args.hostname} · ${of}` : of,
      signalName: of,
      x: state.x0 + 760,
      y: state.y0,
    });
    if (mqtt) {
      link(state, `${mqtt.id}.subscribe`, `${dash.id}.in`);
    }
    linkStacklightCommand(state, {
      pointNodeId: status.id,
      controllerNodeId: controller.id,
      dashNodeId: dash.id,
      mqttNodeId: mqtt?.id,
      zigbee: zigbee,
      x: state.x0 + 760,
      y: state.y0 + 140,
    });
  }
  const next = finishChain(doc, args, state);
  return { ...next, nodes: next.nodes.map(normalizeCatalogNode) };
}
